/**
 * Subscription Paused Blocker
 *
 * Full-screen overlay that blocks access when subscription is paused
 * and the billing period has ended.
 *
 * Features:
 * - Shows pause status and access end date
 * - Provides resume button (redirects to Stripe billing portal)
 * - Cannot be dismissed (full access blocker)
 * - Fully translated (FR/EN)
 */

import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PauseCircle, CreditCard, Info, Loader2, ExternalLink } from 'lucide-react';
import { redirectToBillingPortal } from '@/lib/stripe';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import { useLanguage } from '@/contexts/LanguageContext';

interface SubscriptionPausedBlockerProps {
  currentPeriodEnd: string;
  pausedAt?: string;
}

export function SubscriptionPausedBlocker({
  currentPeriodEnd,
  pausedAt
}: SubscriptionPausedBlockerProps) {
  const { t } = useLanguage();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleResume = async () => {
    try {
      setIsRedirecting(true);
      logger.debug('🔄 Redirecting to billing portal to resume subscription...');

      toast.info(t('subscription.paused.redirecting'));

      await redirectToBillingPortal();
    } catch (error) {
      logger.error('❌ Failed to redirect to billing portal:', error);

      toast.error(
        t('subscription.paused.redirectError'),
        {
          description: t('subscription.paused.redirectErrorDescription')
        }
      );

      setIsRedirecting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full p-8 space-y-6 shadow-2xl">
        {/* Icon and Title */}
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-4 bg-orange-100 dark:bg-orange-900/20 rounded-full">
              <PauseCircle className="w-12 h-12 text-orange-600 dark:text-orange-400" />
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-bold mb-2">
              {t('subscription.paused.title')}
            </h2>
            <p className="text-muted-foreground text-lg">
              {t('subscription.paused.description')}
            </p>
          </div>
        </div>

        {/* Access End Date */}
        <Alert className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
          <Info className="h-5 w-5 text-orange-600" />
          <AlertDescription className="ml-2">
            <div className="space-y-2">
              <p className="font-semibold text-orange-900 dark:text-orange-100">
                {t('subscription.paused.accessEnded')}
              </p>
              <p className="text-xl font-bold text-orange-700 dark:text-orange-300">
                {formatDate(currentPeriodEnd)}
              </p>
              {pausedAt && (
                <p className="text-sm text-orange-600 dark:text-orange-400">
                  {t('subscription.paused.pausedSince', {
                    date: formatDate(pausedAt)
                  })}
                </p>
              )}
            </div>
          </AlertDescription>
        </Alert>

        {/* Resume Instructions */}
        <div className="space-y-3 text-center">
          <p className="text-sm text-muted-foreground">
            {t('subscription.paused.resumeInstructions')}
          </p>

          <Button
            onClick={handleResume}
            disabled={isRedirecting}
            className="w-full"
            size="lg"
          >
            {isRedirecting ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {t('subscription.paused.redirecting')}
              </>
            ) : (
              <>
                <CreditCard className="mr-2 h-5 w-5" />
                {t('subscription.paused.resumeButton')}
                <ExternalLink className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground italic">
            {t('subscription.paused.helpText')}
          </p>
        </div>

        {/* Support Contact */}
        <div className="pt-4 border-t border-border text-center">
          <p className="text-sm text-muted-foreground">
            {t('subscription.paused.needHelp')}{' '}
            <a
              href="mailto:contact@subvention360.com"
              className="text-primary hover:underline font-medium"
            >
              contact@subvention360.com
            </a>
          </p>
        </div>
      </Card>
    </div>
  );
}
